/* hs-eslint ignored failing-rules */
/* eslint-disable no-duplicate-imports */
/* eslint-disable react/prop-types */
/* eslint-disable hubspot-dev/no-confusing-browser-globals */

'use es6';

import { List, Map as ImmutableMap } from 'immutable';
import { Fragment, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import count from 'transmute/count';
import PropTypes from 'prop-types';
import { NavMarker } from 'react-rhumb';
import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import FileMetadataRecord from 'conversations-internal-schema/file-metadata/records/FileMetadataRecord';
import { getVisitorLastReadAtTimestamp, getFileAttachments } from 'conversations-message-history/thread-history/operators/getters';
import MessageHistory from 'conversations-message-history/web/message-history/components/MessageHistory';
import ThreadHistoryScrollWrapper from './ThreadHistoryScrollWrapper';
import { useSenders } from 'conversations-thread-data/senders/hooks/useSenders';
import { useLegacyRecipients } from 'conversations-thread-data/legacy-recipients/hooks/useLegacyRecipients';
import { useFileAttachments } from 'composer-data/attachments/public/hooks';
import { getFileIdsFromHistory } from 'conversations-thread-data/thread-history/public/operators';
import { getData } from 'conversations-async-data/async-data/operators/getters';
import { isSucceeded } from 'conversations-async-data/async-data/operators/statusComparators';
import { getSenderLocatorsFromHistory } from 'conversations-thread-data/senders/operators/getSenderLocatorsFromHistory';
import { getOffsetTimestamp, getOffsetOrdinal, getHasMore } from 'conversations-message-history/thread-history/operators/getters';
import { getLatestAgentMessage } from 'conversations-message-history/thread-history/operators/getLatestAgentMessage';
import { getStatusTimestamp } from 'conversations-message-history/common-message-format/operators/commonMessageFormatGetters';
import { getPresentVisitorLocators } from 'conversations-thread-data/presence/public/selectors';
import { getRecipientsFromHistory, getRecipientLocatorsFromHistory } from '../operators/getRecipientsFromHistory';
import { useVisitors } from 'conversations-thread-data/visitor/hooks/useVisitors';
import { gatherPerformanceMetrics } from '../../performance-metrics/operators/gatherPerformanceMetrics';
import { SEND_MESSAGE } from '../../performance-metrics/constants/performanceConstants';
import { getIsUngatedForNextBestActions, getIsUngatedForReplyToAllMessages } from 'conversations-thread-data/auth/public/selectors';
import { getFriendlyNameResults } from 'conversations-message-history/thread-history/operators/getters';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const AgentThreadHistory = ({
  allowContactPromotions = false,
  canRestoreMessagesForThread,
  className,
  currentUserId,
  fetchHistory,
  historyConfig,
  messages,
  portalId,
  threadHistory,
  threadId,
  vid,
  slots,
  getAdditionalPositionDescriptors
}) => {
  //Check to see if there is an active send message performance mark.
  //If so, measure the difference between that mark's start time
  //and the most recent agent message's sent timestamp
  const sendMessageMarks = performance.getEntriesByName(`${SEND_MESSAGE}-start`);
  const sentTimestamp = getStatusTimestamp(getLatestAgentMessage(threadHistory));
  if (sendMessageMarks.length && sendMessageMarks[0].startTime < sentTimestamp) {
    performance.mark(`${SEND_MESSAGE}-stop`, {
      startTime: sentTimestamp
    });
    gatherPerformanceMetrics(SEND_MESSAGE);
  }
  const presentSenderLocators = useSelector(state => getPresentVisitorLocators(state, {
    threadId
  }));
  const senderLocators = useMemo(() => getSenderLocatorsFromHistory(threadHistory), [threadHistory]);
  const recipientLocators = useMemo(() => getRecipientLocatorsFromHistory(threadHistory), [threadHistory]);
  const recipientVids = useMemo(() => getRecipientsFromHistory(threadHistory), [threadHistory]);
  const {
    asyncVisitors: recipientVisitors
  } = useVisitors({
    vids: recipientVids,
    deferred: !recipientVids.size
  });
  const messagesCount = count(messages);
  const fetchOlderHistory = useCallback(() => {
    if (getHasMore(threadHistory)) {
      fetchHistory({
        threadId,
        offsetTimestamp: getOffsetTimestamp(threadHistory),
        offsetOrdinal: getOffsetOrdinal(threadHistory)
      });
    }
  }, [fetchHistory, threadHistory, threadId]);
  const {
    senders
  } = useSenders({
    senderLocators,
    vid
  });
  const {
    recipients
  } = useLegacyRecipients({
    recipientLocators
  });
  const fileAttachments = getFileAttachments(threadHistory) || ImmutableMap(); // need Map fallback for deepmerge
  const fileIdsFromHistory = useMemo(() => getFileIdsFromHistory(threadHistory), [threadHistory]);
  const {
    fileAttachments: asyncFileAttachments
  } = useFileAttachments({
    fileAttachmentIds: fileIdsFromHistory
  });
  const loadedFileAttachments = useMemo(() => fileAttachments.mergeDeep(asyncFileAttachments.filter(isSucceeded).map(getData).filter(Boolean)), [fileAttachments, asyncFileAttachments]);
  const castFileAttachments = useMemo(() => loadedFileAttachments.map(attachment => attachment === null || attachment === void 0 ? void 0 : attachment.update('fileMetadata', metadata => metadata && new FileMetadataRecord(metadata.toJS()))), [loadedFileAttachments]);
  const ungatedForNextBestActions = useSelector(getIsUngatedForNextBestActions);
  const ungatedForReplyToAllMessages = useSelector(getIsUngatedForReplyToAllMessages);
  const friendlyNames = getFriendlyNameResults(threadHistory);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: "CHAT_FEED_SUCCESS"
    }), /*#__PURE__*/_jsx(ThreadHistoryScrollWrapper, {
      currentUserId: currentUserId,
      fetchOlderHistory: fetchOlderHistory,
      messages: messages,
      persistedMessageCount: messagesCount,
      status: status,
      children: /*#__PURE__*/_jsx(MessageHistory, {
        allowContactPromotions: allowContactPromotions,
        canRestoreMessagesForThread: canRestoreMessagesForThread,
        className: className,
        fileAttachments: castFileAttachments,
        friendlyNames: friendlyNames,
        historyConfig: historyConfig,
        indexedAsyncFileAttachments: asyncFileAttachments,
        messages: messages,
        portalId: portalId,
        senders: senders,
        presentSenderLocators: presentSenderLocators,
        visitorLastReadTimestamp: getVisitorLastReadAtTimestamp(threadHistory),
        getAdditionalPositionDescriptors: getAdditionalPositionDescriptors,
        vid: vid,
        recipientVisitors: recipientVisitors,
        ungatedForNextBestActions: ungatedForNextBestActions,
        ungatedForReplyToAllMessages: ungatedForReplyToAllMessages,
        threadHistory: threadHistory,
        threadId: threadId,
        legacyRecipients: recipients,
        slots: slots
      })
    }, `${threadId}`)]
  });
};
AgentThreadHistory.displayName = 'AgentThreadHistory';
AgentThreadHistory.propTypes = {
  allowContactPromotions: PropTypes.bool,
  canRestoreMessagesForThread: PropTypes.bool,
  className: PropTypes.string,
  fetchHistory: PropTypes.func.isRequired,
  getAdditionalPositionDescriptors: PropTypes.func.isRequired,
  historyConfig: PropTypes.object.isRequired,
  messages: PropTypes.instanceOf(List),
  portalId: PropTypes.number.isRequired,
  threadHistory: RecordPropType('ThreadHistory').isRequired,
  threadId: PropTypes.string.isRequired,
  vid: PropTypes.number.isRequired
};
export default AgentThreadHistory;